import React, { useEffect, useMemo, useRef, useState } from "react";
import functions from "../../utils/functions";
import LeftSideBar from "../../Components/Common/LeftSideBar";
import TopBar from "../../Components/Common/TopBar";
import PageHeader from "../../Components/Common/PageHeader";
import RightSideBar from "../../Components/Common/RightSideBar";
import Notification from "../../Components/Common/Notification";
import { useRevenueAnalysis } from "../../Context/Reports/RevenueAnalysisContext";
import RevenueReportFilter from "../../Components/Reports/RevenueReportFilter";
import { formatDate, numberFormat } from "../../utils/araknerdlib";
import StatCard from "../../Components/Cards/StatCard";

const RevenueAnalysisPage = () => {
  const printRef = useRef(null);

  const {reportData, getRevenueAnalysis} = useRevenueAnalysis();

  useEffect(() => {
    getRevenueAnalysis();
  }, []);

  // ---------- helpers ----------
  const toNumber = (v) => {
    if (v === null || v === undefined) return 0;
    const n = typeof v === "string" ? Number(v) : Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const money = (v) => {
    const n = toNumber(v);
    // You can swap currency formatting if needed
    return n.toLocaleString("en-UG", { style: "currency", currency: "UGX", minimumFractionDigits: 0 });
  };

  const pct = (num, den) => {
    const n = toNumber(num);
    const d = toNumber(den);
    if (d <= 0) return "0%";
    return `${((n / d) * 100).toFixed(1)}%`;
  };

  const safeProducts = reportData?.by_products?.products || [];
  const stickerTotal = reportData?.by_products?.stickers_total || null;
  const stickerBreakdown = reportData?.by_products?.stickers_breakdown || [];
  const streamsObj = reportData?.by_streams || {};

  const totals = useMemo(() => {
    // Total from products + sticker total (because sticker total is separate)
    const p = safeProducts.reduce(
      (acc, x) => {
        acc.invoiced += toNumber(x.amount_invoiced);
        acc.collected += toNumber(x.amount_collected);
        acc.uncollected += toNumber(x.amount_uncollected);
        acc.count += toNumber(x.invoices_count);
        return acc;
      },
      { invoiced: 0, collected: 0, uncollected: 0, count: 0 }
    );

    if (stickerTotal) {
      p.invoiced += toNumber(stickerTotal.amount_invoiced);
      p.collected += toNumber(stickerTotal.amount_collected);
      p.uncollected += toNumber(stickerTotal.amount_uncollected);
      p.count += toNumber(stickerTotal.invoices_count);
    }

    return p;
  }, [safeProducts, stickerTotal]);

  const streams = useMemo(() => {
    const arr = Object.keys(streamsObj || {}).map((k) => ({
      key: k,
      name: streamsObj[k]?.name || k,
      amount: toNumber(streamsObj[k]?.amount),
      count: toNumber(streamsObj[k]?.count),
    }));

    // Sort high → low by amount
    arr.sort((a, b) => b.amount - a.amount);
    return arr;
  }, [streamsObj]);

  const totalStreamAmount = useMemo(() => {
    return streams.reduce((s, x) => s + x.amount, 0);
  }, [streams]);


  const exportPdf = () => {
    // Print only the report area (Save as PDF in browser print dialog)
    window.print();
  };
  

  
  const ProductsTable = () => {
    if (!safeProducts.length && !stickerTotal) return null;

    const rows = [...safeProducts];
    if (stickerTotal) rows.push(stickerTotal);

    return (
      <div className="card custom-card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h6 className="mb-0">Revenue by Product</h6>
          <span className="tx-12 text-muted">
            {formatDate(reportData?.filters?.opening_date)} → {formatDate(reportData?.filters?.closing_date)}
          </span>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover table-bordered mb-0">
              <thead>
                <tr>
                  <th>Product</th>
                  <th className="text-right">Invoices</th>
                  <th className="text-right">Invoiced</th>
                  <th className="text-right">Collected</th>
                  <th className="text-right">Uncollected</th>
                  <th className="text-right">Collection %</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((x) => {
                  const inv = toNumber(x.amount_invoiced);
                  const col = toNumber(x.amount_collected);
                  const uncol = toNumber(x.amount_uncollected);

                  return (
                    <tr key={`${x.invoice_type_id}-${x.product}`}>
                      <td>
                        <div className="d-flex flex-column">
                          <span className="font-weight-semibold">{x.product}</span>
                          <small className="text-muted">Type #{x.invoice_type_id}</small>
                        </div>
                      </td>
                      <td className="text-right">{numberFormat(x.invoices_count)}</td>
                      <td className="text-right font-weight-semibold">{money(inv)}</td>
                      <td className="text-right text-success font-weight-semibold">{money(col)}</td>
                      <td className="text-right text-danger">{money(uncol)}</td>
                      <td className="text-right">{pct(col, inv)}</td>
                    </tr>
                  );
                })}
              </tbody>

              <tfoot>
                <tr>
                  <th>Total</th>
                  <th className="text-right">{numberFormat(totals.count)}</th>
                  <th className="text-right">{money(totals.invoiced)}</th>
                  <th className="text-right text-success">{money(totals.collected)}</th>
                  <th className="text-right text-danger">{money(totals.uncollected)}</th>
                  <th className="text-right">{pct(totals.collected, totals.invoiced)}</th>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Optional mini insight bar */}
          <div className="mt-3">
            <div className="d-flex justify-content-between mb-1">
              <small className="text-muted">Overall Collection Rate</small>
              <small className="font-weight-semibold">{pct(totals.collected, totals.invoiced)}</small>
            </div>
            <div className="progress" style={{ height: 8 }}>
              <div
                className="progress-bar"
                role="progressbar"
                style={{ width: pct(totals.collected, totals.invoiced) }}
                aria-valuenow={toNumber(totals.collected)}
                aria-valuemin="0"
                aria-valuemax={toNumber(totals.invoiced)}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const StreamsCard = () => {
    if (!streams.length) return null;

    return (
      <div className="card custom-card">
        <div className="card-header">
          <h6 className="mb-0">Revenue by Payment Stream</h6>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-sm table-bordered mb-0">
              <thead>
                <tr>
                  <th>Stream</th>
                  <th className="text-right">Amount</th>
                  <th className="text-right">Share</th>
                </tr>
              </thead>
              <tbody>
                {streams.map((s) => (
                  <tr key={s.key}>
                    <td>
                      <div className="d-flex flex-column">
                        <span className="font-weight-semibold">{s.name}</span>
                        <small className="text-muted">{s.key}</small>
                      </div>
                    </td>
                    <td className="text-right font-weight-semibold">{money(s.amount)}</td>
                    <td className="text-right">{pct(s.amount, totalStreamAmount)}</td>
                  </tr>
                ))}
              </tbody>
              {/* <tfoot>
                <tr>
                  <th>Total</th>
                  <th className="text-right">{money(totalStreamAmount)}</th>
                  <th className="text-right">100%</th>
                </tr>
              </tfoot> */}
            </table>
          </div>
          <small className="text-muted d-block mt-2">
            Tip: this helps validate which channels are driving collections (Cash, Mobile Money, Clearances, etc.).
          </small>
        </div>
      </div>
    );
  };

  const StickerBreakdown = () => {
    if (!stickerBreakdown.length) return null;

    return (
      <div className="card custom-card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h6 className="mb-0">Sticker Breakdown</h6>
          {stickerTotal ? (
            <span className="badge badge-primary">
              Sticker Total: {money(stickerTotal.amount_collected)}
            </span>
          ) : null}
        </div>

        <div className="card-body">
          <div className="table-responsive overflow-auto" style={{ maxHeight: '260px' }}>
            <table className="table table-hover table-bordered mb-0">
              <thead>
                <tr>
                  <th>Sticker Type</th>
                  <th className="text-right">Sales</th>
                  <th className="text-right">Invoices</th>
                  <th className="text-right">Invoiced</th>
                  <th className="text-right">Collected</th>
                  <th className="text-right">Uncollected</th>
                </tr>
              </thead>
              <tbody>
                {stickerBreakdown.map((x) => (
                  <tr key={x.sticker_type_id}>
                    <td>
                      <div className="d-flex flex-column">
                        <span className="font-weight-semibold">{x.sticker_type}</span>
                        <small className="text-muted">Type #{x.sticker_type_id}</small>
                      </div>
                    </td>
                    <td className="text-right">{numberFormat(x.sales_count)}</td>
                    <td className="text-right">{numberFormat(x.invoices_count)}</td>
                    <td className="text-right">{money(x.amount_invoiced)}</td>
                    <td className="text-right text-success">{money(x.amount_collected)}</td>
                    <td className="text-right text-danger">{money(x.amount_uncollected)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <small className="text-muted d-block mt-2">
            Since stickers are typically fully paid, this section is great for volume + product-mix analysis.
          </small>
        </div>
      </div>
    );
  };

  const buttons = () => {
  return (
      <>
          <button className='btn btn-light' onClick={exportPdf}><i className='fa fa-file-pdf mr-2' /> Export PDF</button>
          <button className='btn btn-light' onClick={()=>getRevenueAnalysis(true)}><i className='fa fa-redo mr-2' /> Refresh</button>
          <a href="#" className="btn ripple btn-secondary navresponsive-toggler" data-toggle="collapse" data-target="#revenueReportFilterBar"
              aria-controls="navbarSupportedContent" aria-expanded="false" aria-label="Toggle navigation">
              <i className="fe fe-filter mr-1"></i>  Filter <i className="fas fa-caret-down ml-1"></i>
          </a>
          

      </>
  )
}

  return (
    <div className="page revenue-analysis-page">
      <LeftSideBar />

      <div className="main-content side-content pt-0" onClick={functions.closeSystemDrawer}>
        <TopBar />

        <div className="container-fluid">
          <PageHeader title="Revenue Analysis" previousPageTitle="Dashboard" back="/finance"  buttons={buttons()} />

          <RevenueReportFilter />

              {/* KPI Row */}
              <div className="row">
                <StatCard title="Total Invoiced" value={numberFormat(totals.invoiced)} iconClass="fa fa-file-invoice" subtitle="Amount (UGX)" loading={!reportData} />
                <StatCard
                  title="Total Collected"
                  value={numberFormat(totals.collected)}
                  iconClass="fa fa-check-circle"
                  textClass="text-success"
                  subtitle="Amount (UGX)"
                  loading={!reportData}
                />
                <StatCard
                  title="Total Uncollected"
                  value={numberFormat(totals.uncollected)}
                  iconClass="fa fa-exclamation-circle"
                  textClass="text-danger"
                  subtitle="Amount (UGX)"
                  loading={!reportData}
                />
                <StatCard
                  title="Collection Rate"
                  value={pct(totals.collected, totals.invoiced)}
                  subtitle={`${numberFormat(totals.count)} invoices`}
                  iconClass="fa fa-percentage"
                  textClass="text-dark"
                  loading={!reportData}
                />
              </div>
          {functions.isObject(reportData) && <>

              {/* Main grid */}
              <div className="row">
                <div className="col-sm-12 col-xl-4 col-lg-4">{StreamsCard()}</div>
                <div className="col-sm-12 col-xl-8 col-lg-8">
                    {ProductsTable()}
                    {StickerBreakdown()}
                </div>
              </div>
            </>
          }
          {reportData === "404" && <div className="row">
                <div className="col-12">
                  <div className="card custom-card">
                    <div className="card-body">
                      <h5 className="mb-1">No reportData for selected range</h5>
                      <p className="text-muted mb-0">Try adjusting the date range and click Apply.</p>
                    </div>
                  </div>
                </div>
              </div>}
          

          {/* PRINT AREA (same reportData, cleaner for PDF) */}
          <div className="print-area" ref={printRef}>
            {functions.isObject(reportData) ? (
              <div className="p-4">
                <h3 className="mb-1">PMIS Revenue Analysis Report</h3>
                <p className="text-muted">
                  Range: <strong>{formatDate(reportData.filters.opening_date)}</strong> to <strong>{formatDate(reportData.filters.closing_date)}</strong>
                </p>

                <hr />

                <div className="row">
                  <div className="col-6">
                    <p className="mb-1">
                      <strong>Total Invoiced:</strong> {money(totals.invoiced)}
                    </p>
                    <p className="mb-1">
                      <strong>Total Collected:</strong> {money(totals.collected)}
                    </p>
                    <p className="mb-1">
                      <strong>Total Uncollected:</strong> {money(totals.uncollected)}
                    </p>
                    <p className="mb-1">
                      <strong>Collection Rate:</strong> {pct(totals.collected, totals.invoiced)}
                    </p>
                  </div>
                  <div className="col-6 text-right">
                    <p className="mb-1">
                      <strong>Total Invoices:</strong> {numberFormat(totals.count)}
                    </p>
                  </div>
                </div>

                <h5 className="mt-4 mb-2">By Products</h5>
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th className="text-right">Invoices</th>
                      <th className="text-right">Invoiced</th>
                      <th className="text-right">Collected</th>
                      <th className="text-right">Uncollected</th>
                      <th className="text-right">Collection %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...safeProducts, ...(stickerTotal ? [stickerTotal] : [])].map((x) => (
                      <tr key={`${x.invoice_type_id}-${x.product}-print`}>
                        <td>{x.product}</td>
                        <td className="text-right">{numberFormat(x.invoices_count)}</td>
                        <td className="text-right">{money(x.amount_invoiced)}</td>
                        <td className="text-right">{money(x.amount_collected)}</td>
                        <td className="text-right">{money(x.amount_uncollected)}</td>
                        <td className="text-right">{pct(x.amount_collected, x.amount_invoiced)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <h5 className="mt-4 mb-2">By Payment Streams</h5>
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>Stream</th>
                      <th className="text-right">Amount</th>
                      <th className="text-right">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {streams.map((s) => (
                      <tr key={`${s.key}-print`}>
                        <td>{s.name}</td>
                        <td className="text-right">{money(s.amount)}</td>
                        <td className="text-right">{pct(s.amount, totalStreamAmount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {stickerBreakdown.length ? (
                  <>
                    <h5 className="mt-4 mb-2">Sticker Breakdown</h5>
                    <table className="table table-bordered">
                      <thead>
                        <tr>
                          <th>Sticker Type</th>
                          <th className="text-right">Sales</th>
                          <th className="text-right">Invoices</th>
                          <th className="text-right">Invoiced</th>
                          <th className="text-right">Collected</th>
                          <th className="text-right">Uncollected</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stickerBreakdown.map((x) => (
                          <tr key={`${x.sticker_type_id}-print`}>
                            <td>{x.sticker_type}</td>
                            <td className="text-right">{numberFormat(x.sales_count)}</td>
                            <td className="text-right">{numberFormat(x.invoices_count)}</td>
                            <td className="text-right">{money(x.amount_invoiced)}</td>
                            <td className="text-right">{money(x.amount_collected)}</td>
                            <td className="text-right">{money(x.amount_uncollected)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                ) : null}

                <small className="text-muted d-block mt-4">
                  Generated on: {new Date().toLocaleString()}
                </small>
              </div>
            ) : null}
          </div>

          {/* Print styles (hide app chrome, show print area only) */}
          <style>{`
            /* Hide the print area in normal view */
            .print-area { display: none; }

            @media print {
              /* Hide the app chrome */
              .page > .app-sidebar,
              .leftside-menu,
              .main-header,
              .header,
              .topbar,
              .right-sidebar,
              .sidebar,
              .side-menu,
              .navbar,
              .main-sidemenu,
              .main-sidebar,
              .main-content .container-fluid > :not(.print-area) {
                display: none !important;
              }

              /* Show only print content */
              .print-area {
                display: block !important;
              }

              /* Remove margins/padding that waste space */
              body {
                background: #fff !important;
              }

              .main-content, .container-fluid {
                padding: 0 !important;
                margin: 0 !important;
              }

              .table {
                font-size: 12px;
              }
            }
          `}</style>
        </div>
      </div>

      <RightSideBar />
    </div>
  );
};

export default RevenueAnalysisPage;
